"use client";

import React, { useMemo, useState } from "react";

import {
  PaymentCalculator,
  PaymentData,
} from "@/backend/domains/subscription/application/payment.calculator";
import { paymentPlanPeriods, PeriodItem } from "@/backend/domains/subscription/data/paymentPlans";
import { IPaymentPlan } from "@/backend/domains/subscription/domain/PaymentPlan";
import { PaymentSystemScript } from "@/client/domains/payment-system";
import { usePromocodeStore } from "@/client/domains/promocode/domain/store";
import { useModal } from "@/client/shared/hooks/useModal";
import { useNonceStore } from "@/client/stores/nonce";

import { cn } from "@/lib/utils";
import { BaseStoreProps, createAdvancedStore } from "@/stores/createAdvancedStore";
import { useSubscriptionStore } from "@/stores/subscription.store";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  AnyTabsDefinition,
  CurrentTab,
  ExtractTabsType,
  Tabs,
  useTabs,
  UseTabsReturn,
} from "@/components/tabs";

import { CheckoutTab, CheckoutTabProps } from "./tabs/CheckoutTab";
import { IndexTab } from "./tabs/IndexTab";
import { InfoTab, InfoTabProps } from "./tabs/Info";
import { PaymentCardsTab, PaymentCardsTabProps } from "./tabs/PaymentCardsTab";

//

const tabs = {
  indexTab: () => <IndexTab />,
  paymentCardsTab: (props: PaymentCardsTabProps) => <PaymentCardsTab {...props} />,
  checkoutTab: (props: CheckoutTabProps) => <CheckoutTab {...props} />,
  infoTab: (props: InfoTabProps) => <InfoTab {...props} />,
} satisfies AnyTabsDefinition;

//

type Tabs = ExtractTabsType<typeof tabs>;
export type PlanActivationTabsModel = UseTabsReturn<Tabs>;

interface PlanActivationStore extends BaseStoreProps {
  tabsModel: PlanActivationTabsModel;
  plan: IPaymentPlan;
  periodItem: PeriodItem;
  paymentData: PaymentData;
  setPeriod: (periodItem: PeriodItem) => void;
  setPeriodById: (periodId: string) => void;
}

const planActivationStore = createAdvancedStore<PlanActivationStore>("PlanActivationStore");
export const usePlanActivationStore = planActivationStore.useContext;

//

interface ProviderProps {
  children?: React.ReactNode;
  plan: IPaymentPlan;
  periodItem: PeriodItem;
}

const Provider: React.FC<ProviderProps> = ({ children, periodItem: defaultPeriod, plan }) => {
  const subscription = useSubscriptionStore();
  const [periodItem, setPeriodItem] = useState(defaultPeriod);
  const nonce = useNonceStore((s) => s.nonce);
  const promocode = usePromocodeStore((s) => s.appliedPromocode?.promocode);

  const tabsModel = useTabs({
    tabs,
    defaultProps: useMemo(() => [] as const, []),
    defaultTab: "indexTab",
  });

  const paymentData = useMemo(() => {
    const calculator = new PaymentCalculator({
      plan,
      periodItem,
      currentSubscription: subscription,
      currentInvoiceData: subscription.invoice.data,
      currentPlan: subscription.paymentPlan,
      promocode,
    });
    return calculator.calcPaymentData();
  }, [periodItem, plan, promocode, subscription]);

  const isOpen = useModal((s) => s.isOpen("activatePlan"));
  const close = useModal((s) => s.close);
  const currentTab = tabsModel.tab.id;

  return (
    <Tabs tabsModel={tabsModel}>
      <planActivationStore.Provider
        value={{
          tabsModel,
          periodItem,
          plan,
          paymentData,
          setPeriod: (period) => setPeriodItem(period),
          setPeriodById: (periodId) => {
            const periodItem = paymentPlanPeriods.find((p) => p.id === periodId);
            if (!periodItem) throw new Error("Period item is undefined");
            setPeriodItem(periodItem);
          },
        }}
      >
        <Dialog
          open={isOpen}
          onOpenChange={close}
          // modal={currentTab !== "checkoutTab" && process.env.NODE_ENV === "development"}
        >
          <DialogContent
            className={cn("max-w-xs", currentTab === "checkoutTab" && "max-w-md")}
            noPadding
          >
            {children}
          </DialogContent>
        </Dialog>
      </planActivationStore.Provider>
      <PaymentSystemScript nonce={nonce} />
    </Tabs>
  );
};

//

export interface PaymentPlanActivationProps {
  plan: IPaymentPlan;
  periodItem: PeriodItem;
}

//

export const PaymentPlanActivationModal: React.FC = () => {
  const data = useModal((s) => s.getData("activatePlan"));

  if (!data) return null;
  const { periodItem, plan } = data;

  return (
    <Provider periodItem={periodItem} plan={plan}>
      <DialogTitle hidden>Активация тарифа</DialogTitle>
      <CurrentTab />
    </Provider>
  );
};
